import { useContext, useState, useEffect, useMemo } from 'react';
import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Collapse,
  Tooltip
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useNavigate } from 'react-router-dom';
import BrightnessLowTwoToneIcon from '@mui/icons-material/BrightnessLowTwoTone';
import TableChartTwoToneIcon from '@mui/icons-material/TableChartTwoTone';
import MessageIcon from '@mui/icons-material/Message';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import GroupsIcon from '@mui/icons-material/Groups';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ForumIcon from '@mui/icons-material/Forum';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import WorkIcon from '@mui/icons-material/Work';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../../../redux/authSlice';
import { incrementCollabUnread, fetchSentCollaborations } from '../../../../redux/collaborationSlice';
import api from '../../../../redux/config';
import { useTranslation } from 'react-i18next';
import { collabSocket } from '../../../../socket/collabSocket';
import { notificationsSocket } from '../../../../socket/notificationsSocket';
import { fetchPendingServiceCount } from '../../../../redux/serviceRequestSlice';
import { isHotelBusinessArea } from 'src/utils/businessArea';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: bold;
    font-size: ${theme.typography.pxToRem(12)};
    color: ${theme.colors.alpha.trueWhite[50]};
    padding: ${theme.spacing(0, 2.5)};
    line-height: 1.4;
  }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      .MuiListItem-root {
        padding: 0.2px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }

        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }

          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector((x) => x.user.role);
  const profile = useSelector((s) => s.user.profile);
  const isHotel = isHotelBusinessArea(profile);
  const serviceRequestsEnabled = profile?.company?.serviceRequestsEnabled ?? true;
  const collaborations = useSelector((s) =>
    Array.isArray(s.collaborations?.collaborations) ? s.collaborations.collaborations : []
  );
  const pendingServiceCount = useSelector((s) => s.serviceRequests?.pendingCount || 0);
  const { closeSidebar } = useContext(SidebarContext);
  const [open, setOpen] = useState(false);
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [pendingCollabCount, setPendingCollabCount] = useState(0);

  const totalUnread = collaborations.reduce((sum, c) => sum + (c.adminUnreadCount || 0), 0);

  const pendingRequests = useMemo(() => {
    let count = 0;
    collaborations.forEach((c) => {
      ['disease', 'absence', 'dayOff', 'end'].forEach((type) => {
        count += (c[type] || []).filter((i) => i.status === 'Pending').length;
      });
    });
    return count;
  }, [collaborations]);

  useEffect(() => {
    dispatch(fetchSentCollaborations());
  }, [dispatch]);

  // Pending received-collaborations badge (job applications awaiting review).
  // Fetched directly via api instead of the collaborations redux slice
  // because fetchReciveidCollaborations writes into the same state.collaborations
  // field used above for sent-collaboration counts.
  // Backend emits 'notificationAdded' (kind: 'newApplication') on the
  // /collaboration socket namespace when a candidate applies
  // (collaboration.service.ts add() -> sendDirectNotif -> handleNotificationAdded),
  // so we refetch on that event for a live update. Accept/reject on this
  // endpoint (acceptCollaborationSent/rejectCollaborationsent) emit no socket
  // event, so refetching on mount covers those once the admin navigates back.
  useEffect(() => {
    if (role !== 'ADMIN') return undefined;
    const fetchCount = () => {
      api
        .get('/collaboration/recived')
        .then((response) => {
          const data = response.data?.data || response.data || [];
          const list = Array.isArray(data) ? data : [];
          setPendingCollabCount(
            list.filter((c) => c.status === 'Pending').length
          );
        })
        .catch((error) => {
          console.error('Error fetching received collaborations count:', error);
        });
    };
    fetchCount();
    const handleNewApplication = (payload) => {
      if (payload?.notification?.kind === 'newApplication') fetchCount();
    };
    collabSocket.on('notificationAdded', handleNewApplication);
    window.addEventListener('collab:pendingChanged', fetchCount);
    return () => {
      collabSocket.off('notificationAdded', handleNewApplication);
      window.removeEventListener('collab:pendingChanged', fetchCount);
    };
  }, [role]);

  // Pending waiter-call / room-service badge: load the count on mount and
  // keep it fresh via the company-room socket event. The count endpoint is
  // ADMIN/MANAGER-only, so skip the fetch for other roles.
  useEffect(() => {
    const companyId = profile?.company?._id;
    if (!companyId || !(role === 'ADMIN' || role === 'MANAGER')) return undefined;
    const fetchCount = () => dispatch(fetchPendingServiceCount({ companyId, isHotel }));
    fetchCount();
    const event = isHotel ? 'room-service:requested' : 'waiter:called';
    notificationsSocket.on(event, fetchCount);
    return () => {
      notificationsSocket.off(event, fetchCount);
    };
  }, [dispatch, profile, role, isHotel]);

  useEffect(() => {
    const handler = (payload) => {
      const collabId = payload.id || payload.message?.chat || payload.file?.chat;
      if (collabId) dispatch(incrementCollabUnread(collabId));
    };
    collabSocket.on('newMessage', handler);
    collabSocket.on('newFile', handler);
    return () => {
      collabSocket.off('newMessage', handler);
      collabSocket.off('newFile', handler);
    };
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClick1 = () => {
    setOpen1(!open1);
  };

  const handleClick2 = () => {
    setOpen2(!open2);
  };
  return (
    <>
      <MenuWrapper>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.dashboard')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div" id="general">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/tasks"
                  startIcon={
                    <BrightnessLowTwoToneIcon style={{ color: '#18E68E' }} />
                  }
                >
                  {t('sidebar.general')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.accounts')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div" id="profile">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/settings"
                  startIcon={<AccountBoxIcon style={{ color: '#18E68E' }} />}
                >
                  {t('sidebar.profile')}
                </Button>
              </ListItem>
              <ListItem component="div" id="company">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/company/settings"
                  startIcon={<ApartmentIcon style={{ color: '#18E68E' }} />}
                >
                  {t('sidebar.company')}
                </Button>
              </ListItem>
              <ListItem component="div" id="lost-items">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/company/lost-items"
                  startIcon={
                    <Inventory2OutlinedIcon style={{ color: '#18E68E' }} />
                  }
                >
                  {t('sidebar.lost_items')}
                </Button>
              </ListItem>
              {!isHotel && (
                <ListItem component="div" id="restaurant-menu">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company/restaurant-menu"
                    startIcon={
                      <RestaurantMenuIcon style={{ color: '#18E68E' }} />
                    }
                  >
                    {t('sidebar.restaurant_menu')}
                  </Button>
                </ListItem>
              )}
              <ListItem component="div" id="reservations">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/company/reservations"
                  startIcon={
                    <EventAvailableIcon style={{ color: '#18E68E' }} />
                  }
                >
                  {t('sidebar.reservations')}
                </Button>
              </ListItem>
              {serviceRequestsEnabled && (
                <ListItem component="div" id="service-requests">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company/service-requests"
                    startIcon={
                      isHotel ? (
                        <RoomServiceOutlinedIcon style={{ color: '#18E68E' }} />
                      ) : (
                        <NotificationsActiveOutlinedIcon style={{ color: '#18E68E' }} />
                      )
                    }
                  >
                    {isHotel
                      ? t('sidebar.room_service')
                      : t('sidebar.waiter_calls')}
                    {pendingServiceCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1.5,
                          background: '#18e68e',
                          color: '#000',
                          borderRadius: '10px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8
                        }}
                      >
                        {pendingServiceCount}
                      </Box>
                    )}
                  </Button>
                </ListItem>
              )}
              {(role === 'ADMIN' || role === 'MANAGER') && (
                <ListItem component="div" id="favorite-clients">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company/favorite-clients"
                    startIcon={
                      <FavoriteRoundedIcon style={{ color: '#18E68E' }} />
                    }
                  >
                    {t('sidebar.favorite_clients')}
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.management')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div" id="claims">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/claims"
                  startIcon={
                    <TableChartTwoToneIcon style={{ color: '#18E68E' }} />
                  }
                >
                  {t('sidebar.claims')}
                </Button>
              </ListItem>
              <ListItem component="div" id="claimshistory">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/claimsHistory"
                  startIcon={<UnarchiveIcon style={{ color: '#18E68E' }} />}
                >
                  {t('sidebar.claims_history')}
                </Button>
              </ListItem>
              <ListItem component="div" id="claimsMessanger">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/messenger"
                  startIcon={<MessageIcon style={{ color: '#18E68E' }} />}
                >
                  {t('sidebar.Messages')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {role !== 'CLAIM_MANAGER' && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.claims_manager_section')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
              {role === 'ADMIN' && (
                <ListItem component="div" id="managers">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/managers"
                    startIcon={
                      <ManageAccountsIcon style={{ color: '#18E68E' }} />
                    }
                  >
                    {t('sidebar.managers')}
                  </Button>
                </ListItem>
              )}

              {(role === 'ADMIN' || role === 'MANAGER') && (
                <ListItem component="div" id="responsible">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/responsibles"
                    startIcon={<PersonIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('sidebar.responsibles')}
                  </Button>
                </ListItem>
              )}
              {role !== 'CLAIM_MANAGER' && (
                <ListItem component="div" id="claimmanager">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/ClaimManagers"
                    startIcon={<GroupIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('sidebar.claim_managers')}
                  </Button>
                </ListItem>
              )}
              </List>
            </SubMenuWrapper>
          </List>
        )}

        <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.collaborations')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                {role === 'ADMIN' && (
                  <ListItem component="div" id="collaborationssent">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/SentCollab"
                    startIcon={<WorkspacesIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('sidebar.collaborations')}
                    {totalUnread > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1.5,
                          background: '#18e68e',
                          color: '#000',
                          borderRadius: '10px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8
                        }}
                      >
                        {totalUnread}
                      </Box>
                    )}
                  </Button>
                </ListItem>
              )}
              {role === 'ADMIN' && (
                <ListItem component="div" id="collaborationsReceived">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/ReceivedCollab"
                    startIcon={
                      <MarkEmailUnreadRoundedIcon
                        style={{ color: '#18E68E' }}
                      />
                    }
                  >
                    {t('sidebar.received')}
                    {pendingCollabCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1.5,
                          background: '#18e68e',
                          color: '#000',
                          borderRadius: '10px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          minWidth: 20,
                          height: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.8
                        }}
                      >
                        {pendingCollabCount}
                      </Box>
                    )}
                  </Button>
                </ListItem>
              )}
              <ListItem onClick={handleClick2}>
                <Box display="flex" alignItems="center" width="100%">
                  <Button
                    sx={{
                      justifyContent: 'flex-start',
                      flex: 1,
                      minWidth: 0
                    }}
                    startIcon={<StarRoundedIcon style={{ color: '#18E68E' }} />}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                    >
                      {t('sidebar.staff_ratings')}
                    </Box>
                  </Button>
                  {open2 ? (
                    <ExpandLessIcon style={{ color: '#18E68E' }} />
                  ) : (
                    <ExpandMoreIcon style={{ color: '#18E68E' }} />
                  )}
                </Box>
              </ListItem>
              <Collapse in={open2} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem sx={{ pl: 6 }} component="div" id="staff-ratings">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/staff-ratings"
                      end
                      startIcon={
                        <PersonIcon sx={{ ml: 2, color: '#18E68E' }} />
                      }
                      sx={{
                        pl: 5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'white',
                        justifyContent: 'flex-start',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('sidebar.staff_ratings_clients')}
                      </Box>
                    </Button>
                  </ListItem>
                  <ListItem sx={{ pl: 6 }} component="div" id="staff-ratings-admin">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/staff-ratings/admin"
                      startIcon={
                        <AdminPanelSettingsIcon sx={{ ml: 2, color: '#18E68E' }} />
                      }
                      sx={{
                        pl: 5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'white',
                        justifyContent: 'flex-start',
                        width: '100%',
                        minWidth: 0
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('sidebar.staff_ratings_admin')}
                      </Box>
                    </Button>
                  </ListItem>
                </List>
              </Collapse>
              {role === 'ADMIN' && (
                <ListItem component="div" id="attendance">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/attendance"
                    startIcon={<QrCode2Icon style={{ color: '#18E68E' }} />}
                  >
                    {t('sidebar.attendance')}
                  </Button>
                </ListItem>
              )}
              {role === 'ADMIN' && (
                <ListItem component="div" id="collaborationsjobs">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/JobPosts"
                    startIcon={<WorkIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('sidebar.jobs')}
                  </Button>
                </ListItem>
              )}
              {role === 'ADMIN' && (
                <ListItem component="div" id="available-staff">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/AvailableStaff"
                    startIcon={<PersonAddAlt1Icon style={{ color: '#18E68E' }} />}
                  >
                    {t('sidebar.available_staff')}
                  </Button>
                </ListItem>
              )}

              {role === 'ADMIN' && (
                <>
                  <ListItem onClick={handleClick1}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Button
                        sx={{
                          pl: 5,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          color: 'white',
                          justifyContent: 'flex-start',
                          flex: 1
                        }}
                        startIcon={
                          <GroupsIcon sx={{ Pl: 1, color: '#18E68E' }} />
                        }
                      >
                        {t('sidebar.staff')}
                        {totalUnread > 0 && (
                          <Box component="span" sx={{
                            ml: 1.5,
                            background: '#18e68e',
                            color: '#000',
                            borderRadius: '10px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            minWidth: 20,
                            height: 20,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.8,
                          }}>
                            {totalUnread}
                          </Box>
                        )}
                      </Button>
                      {open1 ? (
                        <ExpandLessIcon style={{ color: '#18E68E' }} />
                      ) : (
                        <ExpandMoreIcon style={{ color: '#18E68E' }} />
                      )}
                    </Box>
                  </ListItem>
                  <Collapse in={open1} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem sx={{ pl: 6 }} component="div" id="ListStaff">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/staff"
                          end
                          startIcon={
                            <ListAltIcon sx={{ ml: 2, color: '#18E68E' }} />
                          }
                          sx={{
                            pl: 5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: 'white',
                            justifyContent: 'flex-start',
                            width: '100%'
                          }}
                        >
                          {t('sidebar.list_of_staff')}
                        </Button>
                      </ListItem>
                      <ListItem sx={{ pl: 4 }} component="div" id="messenger">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/staff/messenger"
                          startIcon={
                            <ForumIcon sx={{ ml: 2, color: '#18E68E' }} />
                          }
                          sx={{
                            pl: 5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: 'white',
                            justifyContent: 'flex-start',
                            width: '100%'
                          }}
                        >
                          {t('sidebar.messenger')}
                          {totalUnread > 0 && (
                            <Box component="span" sx={{
                              ml: 1.5, background: '#18e68e', color: '#000',
                              borderRadius: '10px', fontSize: '0.65rem',
                              fontWeight: 700, minWidth: 20, height: 20,
                              display: 'inline-flex', alignItems: 'center',
                              justifyContent: 'center', px: 0.8
                            }}>
                              {totalUnread}
                            </Box>
                          )}
                        </Button>
                      </ListItem>
                      <ListItem sx={{ pl: 4 }} component="div" id="staff-requests">
                        <Button
                          disableRipple
                          component={RouterLink}
                          onClick={closeSidebar}
                          to="/management/staff/requests"
                          startIcon={
                            <AssignmentLateIcon sx={{ ml: 2, color: '#18E68E' }} />
                          }
                          sx={{
                            pl: 5,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: 'white',
                            justifyContent: 'flex-start',
                            width: '100%'
                          }}
                        >
                          Requests
                          {pendingRequests > 0 && (
                            <Box component="span" sx={{
                              ml: 1.5, background: '#ff9800', color: '#000',
                              borderRadius: '10px', fontSize: '0.65rem',
                              fontWeight: 700, minWidth: 20, height: 20,
                              display: 'inline-flex', alignItems: 'center',
                              justifyContent: 'center', px: 0.8
                            }}>
                              {pendingRequests}
                            </Box>
                          )}
                        </Button>
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              )}

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/staff/group-chat"
                  startIcon={<ForumIcon sx={{ color: '#18E68E' }} />}
                  sx={{
                    pl: 5,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'white',
                    justifyContent: 'flex-start',
                    width: '100%'
                  }}
                >
                  {t('sidebar.groupChat')}
                </Button>
              </ListItem>

            </List>
          </SubMenuWrapper>
        </List>

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.account')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  onClick={handleLogout}
                  startIcon={<LogoutIcon style={{ color: '#18E68E' }} />}
                >
                  {t('sidebar.exit')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
